import React, { useEffect, useState } from 'react';
import Icon from '@mdi/react';
import { mdiInformation } from '@mdi/js';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid, MenuItem, Popover, Select } from '@mui/material';
import PropTypes from 'prop-types';
import PopupTitle from '../common/popup_title';
import {
  useGetOwnedRecipesBoardsQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { lastUsedCopyRecipesBoardIdChanged } from '../../features/recipes/recipes_slice';
import { userIsCreator } from '../../services/users';

const CopyToBoardModal = ({
  recipesBoardId,
  menuSelectionEnabled,
  anchorEl,
  onCopy,
  onDismiss,
  boardTitle,
}) => {
  const dispatch = useDispatch();

  const { data: userProfile } = useGetUserProfileQuery();

  const isSmorgStudio = userIsCreator(userProfile);

  const { data: ownedRecipesBoards } = useGetOwnedRecipesBoardsQuery();

  const eligibleRecipesBoards = (ownedRecipesBoards || [])
    .map((b) => ({
      id: b.id,
      title: b.title,
    }))
    .filter((b) => b.id !== recipesBoardId);

  const lastUsedCopyRecipesBoardId = useSelector(
    (state) => state.recipes.lastUsedCopyRecipesBoardId,
  );
  const bestInitialSelectedRecipesBoardId =
    lastUsedCopyRecipesBoardId && lastUsedCopyRecipesBoardId !== recipesBoardId
      ? lastUsedCopyRecipesBoardId
      : eligibleRecipesBoards[0]?.id;

  const [selectedRecipesBoardId, setSelectedRecipesBoardId] = useState(
    bestInitialSelectedRecipesBoardId,
  );

  const [selectedMenuId, setSelectedMenuId] = useState(null);

  const eligibleMenus = (
    ownedRecipesBoards.find((b) => b.id === selectedRecipesBoardId)?.menus || []
  ).map((m) => ({ id: m.id, title: m.title }));

  useEffect(() => {
    setSelectedRecipesBoardId(bestInitialSelectedRecipesBoardId);
  }, [bestInitialSelectedRecipesBoardId]);

  const bestInitialMenuId = eligibleMenus[0]?.id;

  useEffect(() => {
    if (menuSelectionEnabled) {
      setSelectedMenuId(bestInitialMenuId);
    }
  }, [bestInitialMenuId, menuSelectionEnabled]);

  const clearState = () => {
    setSelectedRecipesBoardId(null);
  };

  const onClose = () => {
    clearState();
    onDismiss();
  };

  const onBoardSelectChange = (event) => {
    setSelectedRecipesBoardId(event.target.value);
  };

  const onMenuSelectChange = (event) => {
    console.log(`onMenuSelectChange ${event.target.value}`);
    if (menuSelectionEnabled) {
      setSelectedMenuId(event.target.value);
    }
  };

  const height = isSmorgStudio ? 290 : 220;

  return (
    <Popover
      open
      PaperProps={{
        style: { width: '320px', height: `${height}px`, padding: '10px' },
      }}
      onClose={onClose}
      onClick={(ev) => ev.stopPropagation()}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}>
      <Grid container style={{ fontSize: '14px' }}>
        <Grid item xs={12}>
          <PopupTitle
            titleText={'Copy to '.concat(boardTitle)}
            closeButtonEnabled
            onClickClose={onClose}
          />
        </Grid>
        <Grid item xs={12} style={{ padding: '10px 5px 5px 5px' }}>
          Select {boardTitle}:
        </Grid>
        <Grid item xs={12} style={{ padding: '0 5px 5px 5px' }}>
          <Select
            variant="standard"
            value={selectedRecipesBoardId}
            onChange={onBoardSelectChange}
            inputProps={{ 'aria-label': 'Without label' }}>
            <MenuItem disabled value="">
              <em>Select board</em>
            </MenuItem>
            {eligibleRecipesBoards.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.title}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        {menuSelectionEnabled && (
          <>
            <Grid item xs={12} style={{ padding: '10px 5px 5px 5px' }}>
              Select menu:
            </Grid>
            <Grid item xs={12} style={{ padding: '0 5px 5px 5px' }}>
              <Select
                variant="standard"
                value={selectedMenuId}
                onChange={onMenuSelectChange}
                inputProps={{ 'aria-label': 'Without label' }}>
                <MenuItem disabled value="">
                  <em>Select menu</em>
                </MenuItem>
                {eligibleMenus.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.title}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </>
        )}
        <Grid item xs={12} style={{ padding: '5px' }}>
          <Button
            onClick={(ev) => {
              dispatch(
                lastUsedCopyRecipesBoardIdChanged(selectedRecipesBoardId),
              );
              if (menuSelectionEnabled) {
                onCopy(selectedRecipesBoardId, selectedMenuId);
              } else {
                onCopy(selectedRecipesBoardId);
              }
              ev.stopPropagation();
            }}
            variant="contained"
            size="medium"
            color="primary"
            disableElevation
            disabled={
              !selectedRecipesBoardId ||
              (menuSelectionEnabled && !selectedMenuId)
            }
            style={{ justifyContent: 'flex-start' }}>
            Copy
          </Button>
        </Grid>
        {isSmorgStudio && (
          <Grid item xs={12} style={{ padding: '5px' }}>
            <Grid container>
              <Grid item xs={1}>
                <Icon
                  path={mdiInformation}
                  style={{
                    width: '24px',
                    height: '24px',
                    color: 'orange',
                  }}
                />
              </Grid>
              <Grid item xs={11}>
                Copied recipes are treated as completely new recipes and will
                not preserve any previous links to programs
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Popover>
  );
};

CopyToBoardModal.propTypes = {
  recipesBoardId: PropTypes.string.isRequired,
  menuSelectionEnabled: PropTypes.bool,
  anchorEl: PropTypes.element.isRequired,
  onCopy: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  boardTitle: PropTypes.string,
};

CopyToBoardModal.defaultProps = {
  menuSelectionEnabled: false,
  boardTitle: 'board',
};

export default CopyToBoardModal;
